import React from 'react';
import * as Sentry from '@sentry/react';
import FullPageErrorScreen from './FullPageErrorScreen';

/**
 * Top-level ErrorBoundary. Catches any uncaught render error in the React
 * tree below it and reports the error to Sentry with a tag identifying
 * which boundary caught it.
 *
 * Why a custom one instead of just Sentry.ErrorBoundary?
 * - Lets us scope recovery to a "Try again" button (resets internal state
 *   and re-renders the children) instead of showing Sentry's dialog or
 *   a static fallback.
 * - Lets us show our own branded error screen.
 * - Still forwards the error to Sentry so the support team is notified.
 *
 * Behavior:
 * - On error: render <FullPageErrorScreen error={error} onRetry={reset} />
 *   (reset() resets internal state so children re-render).
 * - If a custom `fallback` prop is passed, render that instead.
 *   `fallback` may be either:
 *     a) a React element, or
 *     b) a function (error, reset) => ReactElement
 *
 * Sentry integration: every caught error is sent to Sentry with the
 * `boundary` tag set to `name` (or 'root' if not provided) so we can
 * tell which boundary caught which error in the Sentry dashboard.
 *
 * Note: ErrorBoundary must remain a class component. React does not
 * support error boundaries in functional components (yet — React 18's
 * `use()` + error handling might change this in the future).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const name = this.props.name || 'root';
    Sentry.withScope((scope) => {
      scope.setTag('boundary', name);
      scope.setExtra('componentStack', info?.componentStack || '');
      Sentry.captureException(error);
    });

    // Also log to console for local development. (Sentry will redact PII
    // in production, but local logs are useful for debugging.)
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${name}]`, error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onReset === 'function') {
      this.props.onReset();
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { fallback } = this.props;
    if (typeof fallback === 'function') {
      return fallback(this.state.error, this.reset);
    }
    if (fallback) {
      return fallback;
    }
    return <FullPageErrorScreen error={this.state.error} onRetry={this.reset} />;
  }
}

export default ErrorBoundary;
